/** @jsx jsx */
import * as React from 'react';
import { useCombobox } from 'downshift';
import cx from 'classnames';
import upperCaseFirst from 'upper-case-first';
import { jsx, css } from '@emotion/core';
import Boolean from '@clearkit/icons/glyphs/Boolean';
import Caret from '@clearkit/icons/glyphs/Caret';
import Clock from '@clearkit/icons/glyphs/Clock';
import GoExternal from '@clearkit/icons/glyphs/GoExternal';
import Number from '@clearkit/icons/glyphs/Number';
import Select from '@clearkit/icons/glyphs/Select';
import String from '@clearkit/icons/glyphs/String';
import { FixedSizeList as List } from 'react-window';

import {
  defaultItemRenderer,
  defaultAttributeSelectorEmptyState,
} from '../../common/utils';
import { usePopover } from '../../hooks';
import { CKAttributeSelectorMenu } from '../CKAttributeSelectorMenu';
import { CKTag } from '../CKTag';
import {
  AttributeHierarchyItem,
  CKPickerVariant,
  CommonSelectProps,
  HierarchyCategory,
  HierarchyAttribute,
} from '../../common/types';
import {
  filterAttributes,
  getSelectableAttributes,
  groupBy,
  defaultComparator,
  AttributeComparator,
} from './utils';
import classNames from 'classnames';
import { ItemGroup } from '../CKOptionGroupsMenu';

type TypeMismatchCheck = (item: AttributeHierarchyItem) => boolean;

interface CKAttributeSelectorProps
  extends CommonSelectProps<AttributeHierarchyItem> {
  isSmall?: boolean;
  variant?: CKPickerVariant;
  isDisabled?: boolean;
  inputRef?: React.MutableRefObject<HTMLInputElement | undefined>;
  attributeRenderer?: (item: DisplayableAttribute) => string;
  emptyState?: () => React.ReactNode;
  attributes: Array<AttributeHierarchyItem>;
  maxHeight?: number;
  icon?: React.ReactNode;
  rootCategoryIdentifier?: string;
  rootCategoryLabel?: string;
  rootCategoryMeta?: React.ReactNode;
  detailDrawerPosition: 'left' | 'right';
  selectedAttribute?: HierarchyAttribute;
  isTypeMismatch?: TypeMismatchCheck;
  attributeComparator?: AttributeComparator;
  onSelectedItemChange: (
    item: AttributeHierarchyItem | undefined | null
  ) => void;
}

export type DisplayableAttribute = SelectorCategory | HierarchyAttribute;

export type SelectorCategory = {
  type: 'selector_category';
  label: string;
  value: string;
  meta?: React.ReactNode | undefined;
  identifier: string;
  parent: SelectorCategory | null;
} & (
  | {
      children: DisplayableAttribute[];
      subcategories?: undefined;
    }
  | {
      children?: undefined;
      subcategories: SelectorSubcategory[];
    }
);

export type SelectorSubcategory = {
  type: 'selector_subcategory';
  label: string;
  children: DisplayableAttribute[];
};

function allCategories(
  items: AttributeHierarchyItem[]
): items is HierarchyCategory[] {
  return !items.some(a => a.type === 'attribute');
}

type GroupAttributesForDisplayOptions = {
  attributeTree: AttributeHierarchyItem[];
  identifier?: string | 'root';
  label?: string | undefined;
  meta?: React.ReactNode | undefined;
};

function groupAttributesForDisplay({
  attributeTree,
  identifier,
  label,
  meta,
}: GroupAttributesForDisplayOptions): SelectorCategory {
  const syntheticRoot = convertCategoryForDisplay(
    {
      type: 'category',
      identifier: identifier,
      label,
      value: '',
      children: attributeTree,
    } as HierarchyCategory,
    null,
    meta
  );

  // If the root category node only has one child and it is also a category,
  // let's make it the root instead
  if (
    syntheticRoot.children?.length === 1 &&
    syntheticRoot.children[0].type === 'selector_category'
  ) {
    return {
      ...syntheticRoot.children[0],
      parent: null,
      meta,
    };
  } else {
    return syntheticRoot;
  }
}

function convertCategoryForDisplay(
  attribute: HierarchyCategory,
  parent: SelectorCategory | null,
  meta?: React.ReactNode | undefined
): SelectorCategory {
  const category: SelectorCategory = {
    type: 'selector_category',
    identifier: attribute.identifier,
    label: attribute.label,
    value: attribute.value,
    meta,
    parent,
    children: [],
  };
  const attributes = attribute.children;

  const groupedAttributes = groupBy(
    attributes,
    (a: any) => `${a.identifier}:${a.source || a.value}`
  );

  for (const identifier in groupedAttributes) {
    const group = groupedAttributes[identifier];

    if (group.length > 1) {
      if (!allCategories(group)) {
        throw new Error(
          `Multiple attributes with the same identifier: ${group[0].identifier}`
        );
      }

      category.children.push({
        type: 'selector_category',
        identifier: group[0].identifier,
        label: group[0].source as string,
        value: group[0].value,
        parent: category,
        subcategories: group.map(subcategory => {
          return {
            type: 'selector_subcategory',
            label: subcategory.subtypeName,
            children: convertAttributesForDisplay(
              subcategory.children,
              category
            ),
          } as SelectorSubcategory;
        }),
      });
    } else {
      category.children = category.children.concat(
        convertAttributesForDisplay(group, category)
      );
    }
  }

  return category;
}

function convertAttributesForDisplay(
  attributes: AttributeHierarchyItem[],
  parent: SelectorCategory
): DisplayableAttribute[] {
  return attributes.map(attribute => {
    if (attribute.type === 'attribute') {
      return attribute;
    } else {
      return convertCategoryForDisplay(attribute, parent);
    }
  });
}

export const CKAttributeSelector = (
  props: CKAttributeSelectorProps
): React.ReactElement<CKAttributeSelectorProps> => {
  const {
    popperOffset,
    popperModifiers,
    popperPlacement,
    placeholder,
    icon,
    isDisabled,
    popperZIndex,
    attributeRenderer = defaultItemRenderer,
    isTypeMismatch = () => false,
    attributeComparator = defaultComparator,
    emptyState = defaultAttributeSelectorEmptyState,
    variant,
    isSmall = false,
    attributes,
    maxHeight = 250,
    detailDrawerPosition = 'right',
    inputRef,
    selectedAttribute,
    onSelectedItemChange,
    rootCategoryIdentifier,
    rootCategoryLabel,
    rootCategoryMeta,
  } = props;
  const popperReference = React.useRef<HTMLDivElement>(null);
  const popperPopover = React.useRef<HTMLDivElement>(null);

  let calculatedPopperPlacement = popperPlacement;

  if (!calculatedPopperPlacement) {
    calculatedPopperPlacement =
      detailDrawerPosition === 'right' ? 'bottom-start' : 'bottom-end';
  }

  const popover = usePopover({
    popperReference,
    popperPopover,
    popperOffset,
    popperPlacement: calculatedPopperPlacement,
    popperModifiers,
  });

  const listRef = React.useRef<List>();

  const [items, setItems] = React.useState<HierarchyAttribute[]>([]);

  // Building the tree of displayable attributes may be expensive for large
  // lists, so we will temporarily make it null and update in an effect hook
  // whenever the attributes change
  const [
    selectedCategory,
    setSelectedCategory,
  ] = React.useState<SelectorCategory | null>(null);

  const [
    selectedSubcategory,
    setSelectedSubcategory,
  ] = React.useState<SelectorSubcategory | null>(null);

  const [query, setQuery] = React.useState<string | undefined>(undefined);
  const [queryResults, setQueryResults] = React.useState<
    ItemGroup<HierarchyAttribute>[] | HierarchyAttribute[] | null
  >(null);

  const {
    getInputProps,
    getItemProps,
    getLabelProps,
    getMenuProps,
    getToggleButtonProps,
    isOpen,
    highlightedIndex,
    setHighlightedIndex,
    setInputValue,
    selectedItem,
    getComboboxProps,
  } = useCombobox<HierarchyAttribute>({
    items,
    itemToString: (item: any) => (item ? item.label : ''),
    selectedItem: selectedAttribute || null,
    onSelectedItemChange(item) {
      setQuery(undefined);
      onSelectedItemChange(item.selectedItem);
    },
    stateReducer(state, { changes }) {
      if (changes?.selectedItem && isTypeMismatch(changes.selectedItem)) {
        return state;
      }

      const isLabelInputMismatch =
        state.isOpen &&
        !changes.isOpen &&
        changes.selectedItem?.label !== changes.inputValue;

      if (isLabelInputMismatch) {
        setInputValue(selectedAttribute?.label || '');
      }

      return {
        ...state,
        ...changes,
      };
    },
  });

  React.useEffect(() => {
    if (attributes !== null) {
      let topCategory = groupAttributesForDisplay({
        attributeTree: attributes,
        identifier: rootCategoryIdentifier,
        label: rootCategoryLabel,
        meta: rootCategoryMeta,
      });
      setSelectedCategory(topCategory);
      setDefaultSubcategory(topCategory);
      setItems(getSelectableAttributes(attributes));
    }
  }, [attributes, rootCategoryIdentifier, rootCategoryLabel, rootCategoryMeta]);

  React.useEffect(() => {
    setQueryResults(query ? filterAttributes(attributes, query) : null);
  }, [query, attributes]);

  function onCategorySelected(category: SelectorCategory) {
    setSelectedCategory(category);
    setDefaultSubcategory(category);
  }

  function setDefaultSubcategory(category: SelectorCategory) {
    if (category.subcategories) {
      setSelectedSubcategory(category.subcategories[0]);
    } else {
      setSelectedSubcategory(null);
    }
  }

  function onSubcategorySelected(subcategory: SelectorSubcategory) {
    setSelectedSubcategory(subcategory);
  }

  function onBackNavigation() {
    // @ts-ignore
    setHighlightedIndex(-1);

    if (selectedCategory?.parent) {
      onCategorySelected(selectedCategory.parent);
    }
  }

  React.useEffect(() => {
    setTimeout(() => {
      popover.current?.forceUpdate();
    });
  }, [isOpen, popover]);

  const outerClassName = cx('ck-picker', {
    'ck-picker--open': isOpen,
    'ck-picker--error': variant === 'error',
    'ck-picker--success': variant === 'success',
    'ck-picker--disabled': isDisabled,
    'ck-picker--small': isSmall,
  });

  const highlightedItem =
    highlightedIndex == null
      ? null
      : (items[highlightedIndex] as HierarchyAttribute);

  const showMenu = isOpen && attributes && attributes.length > 0;
  const showDetails = showMenu && highlightedItem?.type === 'attribute';

  return (
    <div className={outerClassName} ref={popperReference}>
      <label className="ck-sr-only" {...getLabelProps()}>
        {placeholder}
      </label>
      <div {...getComboboxProps()}>
        <div
          className="ck-picker-toggle ck-picker-toggle--typeable appearance-none"
          {...getToggleButtonProps()}
        >
          {icon && (
            <span className="ck-picker-icon ck-picker-icon--left">{icon}</span>
          )}
          <input
            className="ck-picker-value"
            disabled={isDisabled}
            css={css`
              width: 100%;
            `}
            {...getInputProps({
              placeholder,
              ref: inputRef as React.MutableRefObject<HTMLInputElement>,
              onChange(event: any) {
                const inputQuery =
                  event.target.value === '' ? null : event.target.value;

                setQuery(inputQuery);
              },
            })}
          />
          <SelectedItemIcon selectedItem={selectedItem} />
          <button
            disabled={isDisabled}
            css={css`
              display: flex;
              &:focus {
                outline: 2px solid transparent;
              }
            `}
            aria-label="toggle menu"
          >
            <span className="ck-picker-icon ck-picker-icon--right">
              <Caret />
            </span>
          </button>
        </div>
      </div>
      <div
        {...getMenuProps({ ref: popperPopover })}
        style={{
          visibility: isOpen ? 'visible' : 'hidden',
          pointerEvents: isOpen ? 'auto' : 'none',
          zIndex: popperZIndex,
          overflow: 'initial',
        }}
        className={classNames(
          'ck-popover flex flex-row divide-x divide-gray-200 min-w-full box-content',
          {
            'rounded-l-none': showDetails && detailDrawerPosition === 'left',
            'rounded-r-none': showDetails && detailDrawerPosition === 'right',
          }
        )}
      >
        {isOpen && selectedCategory && (
          <React.Fragment>
            {attributes == null ||
            attributes.length === 0 ||
            (queryResults && queryResults.length === 0) ? (
              emptyState()
            ) : (
              <CKAttributeSelectorMenu
                maxHeight={maxHeight}
                isTypeMismatch={isTypeMismatch}
                getItemProps={getItemProps}
                highlightedAttribute={highlightedItem}
                attributeRenderer={attributeRenderer}
                listRef={listRef}
                itemsForSelection={items}
                selectedAttribute={selectedItem}
                selectedCategory={selectedCategory}
                selectedSubcategory={selectedSubcategory}
                query={query}
                queryResults={queryResults}
                onCategorySelected={onCategorySelected}
                onSubcategorySelected={onSubcategorySelected}
                onBackNavigation={onBackNavigation}
                attributeComparator={attributeComparator}
              >
                {showDetails && (
                  <AttributeSelectorDetailDrawer
                    highlightedItem={highlightedItem}
                    isTypeMismatch={isTypeMismatch}
                    position={detailDrawerPosition}
                  />
                )}
              </CKAttributeSelectorMenu>
            )}
          </React.Fragment>
        )}
      </div>
    </div>
  );
};

// externalTypeTranslations holds presentation strings for external types, so
// we can better match the data users may find in external services.
// Anything not presented in this map is going to be first-letter-capitalized.
const externalTypeTranslations = new Map<string, string>([
  ['datetime', 'Date/Time'],
  ['textarea', 'Text Area'],
  ['string', 'Text'],
  ['boolean', 'Checkbox'],
  ['multipicklist', 'Picklist (Multi-Select)'],
  ['id', 'ID'],
  ['url', 'URL'],
  ['int', 'Integer'],
  ['encryptedstring', 'Text (Encrypted)'],
]);

interface TypePresentation {
  tagClass: string;
  iconType: (props: React.SVGProps<SVGSVGElement>) => JSX.Element;
  label: string;
}

const typePresentations = new Map<string, TypePresentation>([
  [
    'boolean',
    {
      tagClass: 'text-green-700 bg-green-100',
      iconType: Boolean,
      label: 'Boolean',
    },
  ],
  [
    'date',
    {
      tagClass: 'text-yellow-700 bg-yellow-100',
      iconType: Clock,
      label: 'Date',
    },
  ],
  [
    'datetime',
    {
      tagClass: 'text-yellow-700 bg-yellow-100',
      iconType: Clock,
      label: 'Date Time',
    },
  ],
  [
    'number',
    {
      tagClass: 'text-blue-700 bg-blue-100',
      iconType: Number,
      label: 'Number',
    },
  ],
  [
    'select',
    {
      tagClass: 'text-aqua-700 bg-aqua-100',
      iconType: Select,
      label: 'Picklist',
    },
  ],
  [
    'multi_select',
    {
      tagClass: 'text-aqua-700 bg-aqua-100',
      iconType: Select,
      label: 'Multi Picklist',
    },
  ],
  [
    'string',
    {
      tagClass: 'text-purple-700 bg-purple-100',
      iconType: String,
      label: 'Text',
    },
  ],
]);

function AttributeSelectorDetailDrawer({
  highlightedItem,
  isTypeMismatch,
  position,
}: {
  highlightedItem: HierarchyAttribute | null;
  isTypeMismatch: TypeMismatchCheck;
  position: 'left' | 'right';
}) {
  return (
    <div
      className={classNames(
        'flex flex-col w-60 top-0 bottom-0 absolute rounded-md bg-white border-gray-200',
        {
          'left-full rounded-l-none': position === 'right',
          'right-full rounded-r-none': position === 'left',
        }
      )}
      style={{
        boxShadow:
          '0px 1px 3px rgba(89,97,104, .15), 0 3px 15px rgba(89,97,104, .2)',
        clipPath:
          position === 'right'
            ? 'inset(-1px -15px -15px -1px)'
            : 'inset(-1px -1px -15px -3px)',
      }}
    >
      <div
        className={classNames(
          'px-4 py-3 mb-1 text-sm font-medium border-b border-gray-200 bg-gray-0 h-11 truncate',
          {
            'rounded-tr-md': position === 'right',
            'rounded-tl-md': position === 'left',
          }
        )}
      >
        {highlightedItem?.label}
      </div>

      {highlightedItem?.meta?.description && (
        <div className="py-4 mx-4 text-sm border-b border-gray-200">
          {highlightedItem.meta.description}
          {highlightedItem?.meta?.link?.href && (
            <div className="mt-1">
              <a
                href={highlightedItem.meta.link.href}
                target="_blank"
                rel="noreferrer"
                className="flex text-blue"
              >
                Learn more
                <GoExternal className="ml-1" />
              </a>
            </div>
          )}
        </div>
      )}
      <div className="flex flex-row justify-between py-2 mx-4 mb-1 text-sm">
        <p>Data Type</p>

        {(() => {
          const externalType: string =
            highlightedItem?.meta?.externalType || '';
          let externalTypeTranslation:
            | string
            | undefined = externalTypeTranslations.get(externalType);
          if (externalTypeTranslation === undefined) {
            externalTypeTranslation = upperCaseFirst(externalType);
          }

          const dataType: string = highlightedItem?.meta?.dataType || '';
          const presentation:
            | TypePresentation
            | undefined = typePresentations.get(dataType);
          if (presentation === undefined) {
            return <p>{externalTypeTranslation || dataType}</p>;
          } else {
            const Icon = presentation.iconType;
            return (
              <CKTag className={presentation.tagClass}>
                <Icon className="mr-1" />
                {externalTypeTranslation || presentation.label}
              </CKTag>
            );
          }
        })()}
      </div>

      {highlightedItem && isTypeMismatch(highlightedItem) && (
        <div className="pb-4 mx-4 text-sm font-medium text-yellow-500 italic">
          Data type mismatch. This field cannot be used for this mapping.
        </div>
      )}
    </div>
  );
}

function SelectedItemIcon({
  selectedItem,
}: {
  selectedItem: HierarchyAttribute | null;
}) {
  const Component =
    selectedItem?.meta &&
    (() => {
      switch (selectedItem.meta.dataType) {
        case 'boolean':
          return Boolean;
        case 'date':
        case 'datetime':
          return Clock;
        case 'number':
          return Number;
        case 'select':
        case 'multi_select':
          return Select;
        case 'string':
          return String;
        default:
          return null;
      }
    })();

  if (!Component) {
    return null;
  }
  return (
    <span className="ck-picker-icon ck-picker-icon--right">
      <Component className="mr-1" />
    </span>
  );
}
